/* ============================================================
   PintaFlow — Premium Landing Shell (themeable)
   ----------------------------------------------------------
   Same layout & copy. Color story switches via `theme` prop.
   ============================================================ */
import { useState, useEffect } from "react";
import { ArrowUpRight, Sparkles, Loader2, PlayCircle, X } from "lucide-react";
import WalkthroughModal from "@/components/WalkthroughModal";
import { track, EVENTS } from "@/lib/analytics";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/today";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  ["Idea", "Capture every spark, fast."],
  ["Research", "Notes, links, quiet thinking."],
  ["Script", "Where it becomes shape."],
  ["Create", "Record. Edit. Refine."],
  ["Schedule", "Choose the moment."],
  ["Published", "Out in the world."],
];

const PRINCIPLES = [
  "Today Mode — three moves, every morning.",
  "Creator Momentum — five dimensions, one score.",
  "Idea Vault — every spark, kept.",
  "Pipeline — six rooms, one craft.",
  "AI Co-pilot — a script editor with taste.",
  "Streak — quiet consistency, made visible.",
];

export default function PremiumLandingShell({ theme }) {
  const t = theme;
  const ink72 = `${t.ink}B8`;   // primary body (was 60%)
  const ink55 = `${t.ink}8C`;   // secondary (was 40%)
  const ink14 = `${t.ink}24`;   // hairlines (was 12%)
  const [demoLoading, setDemoLoading] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const startGuestDemo = async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    track(EVENTS.GUEST_STARTED, { source: "landing_cta" });
    try {
      // Clear any stale local session so the guest session takes effect cleanly
      localStorage.removeItem("session_token");

      const r = await fetch(`${BACKEND}/api/auth/guest`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("guest failed");
      const data = await r.json();
      if (data.session_token) localStorage.setItem("session_token", data.session_token);

      // Seed demo workspace so the dashboard is immediately rich
      await fetch(`${BACKEND}/api/demo/seed`, {
        method: "POST",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${data.session_token}`,
          "Content-Type": "application/json",
        },
        body: "{}",
      });
      window.location.href = "/today";
    } catch (e) {
      setDemoLoading(false);
      alert("Could not start demo. Please try signing in with Google.");
    }
  };

  return (
    <div
      className="min-h-screen overflow-x-hidden relative"
      style={{
        background: t.bg,
        color: t.ink,
        fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
      }}
    >
      {/* Subtle warm vignette adds depth on big laptop screens — invisible on mobile */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 100% 70% at 50% 0%, ${t.seal}10 0%, transparent 55%)`,
          mixBlendMode: "screen",
        }}
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght,SOFT@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500&display=swap"
        rel="stylesheet"
      />
      <style>{`
        .display-${t.id} { font-family: 'Fraunces', serif; font-variation-settings: 'opsz' 144, 'SOFT' 30, 'wght' 450; letter-spacing: -0.035em; }
        .display-italic-${t.id} { font-family: 'Fraunces', serif; font-variation-settings: 'opsz' 144, 'SOFT' 80, 'wght' 500; font-style: italic; letter-spacing: -0.03em; color: ${t.seal}; }
        .eyebrow-${t.id} { font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 0.34em; text-transform: uppercase; }
      `}</style>

      {/* NAV */}
      <header className="border-b" style={{ borderColor: ink14 }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: t.seal }} />
            <span
              className="text-[17px] tracking-tight"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 24, 'wght' 500" }}
            >
              PintaFlow
            </span>
          </div>
          <button onClick={startGoogleLogin} className="text-sm hover:opacity-60 transition" style={{ color: t.ink }}>
            Sign in
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-24 md:pb-36">
          <div className={`eyebrow-${t.id} mb-8 md:mb-10 flex items-center gap-3`} style={{ color: ink55 }}>
            <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: t.seal }} />
            Volume 02 — The daily creator studio
          </div>

          <h1
            className={`display-${t.id} text-[44px] sm:text-[68px] md:text-[96px] lg:text-[112px] leading-[0.96] max-w-[18ch]`}
          >
            <span className={`display-italic-${t.id}`}>Three</span> moves today.<br />
            Everything else <span className={`display-italic-${t.id}`}>can wait.</span>
          </h1>

          <div className="mt-10 md:mt-14 max-w-[560px]">
            <p className="text-[16px] md:text-[19px] leading-[1.65]" style={{ color: ink72 }}>
              PintaFlow is the calm Creator OS built around one daily ritual — the three growth moves your data actually needs from you today. Vault, Pipeline, Scripts, Analytics — the whole studio feeds that morning promise.
            </p>
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                onClick={startGoogleLogin}
                className="group inline-flex items-center justify-between rounded-full pl-6 pr-4 py-3.5 transition hover:opacity-90 w-full sm:w-auto sm:min-w-[230px]"
                style={{ background: t.ink, color: t.bg }}
                data-testid="landing-signin-btn"
              >
                <span className="text-[14px] font-medium tracking-tight whitespace-nowrap">Start with today&apos;s 3 moves</span>
                <span
                  className="w-7 h-7 rounded-full ml-6 flex items-center justify-center transition group-hover:rotate-45"
                  style={{ background: t.seal, color: t.bg }}
                >
                  <ArrowUpRight size={13} strokeWidth={1.75} />
                </span>
              </button>
              <button
                onClick={startGuestDemo}
                disabled={demoLoading}
                className="group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 transition hover:bg-opacity-10 w-full sm:w-auto whitespace-nowrap disabled:opacity-70"
                style={{ border: `1px solid ${t.seal}`, color: t.seal, background: "transparent" }}
                data-testid="landing-guest-demo-btn"
              >
                {demoLoading ? (
                  <>
                    <Loader2 size={13} className="animate-spin" strokeWidth={2} />
                    <span className="text-[14px] font-medium tracking-tight">Loading demo…</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={13} strokeWidth={1.8} />
                    <span className="text-[14px] font-medium tracking-tight">Try the live demo</span>
                  </>
                )}
              </button>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <span className={`eyebrow-${t.id}`} style={{ color: ink55 }}>
                No signup needed · Sign in with Google to save your work
              </span>
              <button
                onClick={() => setShowVideo(true)}
                className="inline-flex items-center gap-1.5 text-[12px] transition hover:opacity-80"
                style={{ color: t.seal }}
                data-testid="landing-watch-walkthrough-btn"
              >
                <PlayCircle size={13} strokeWidth={1.6} />
                Watch the 60-second walkthrough
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="border-t" style={{ borderColor: ink14 }} />

      {/* NEW: THE DAILY RITUAL — Today Mode showcase (the unique wedge) */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 md:mb-20">
          <div className="md:col-span-3">
            <div className={`eyebrow-${t.id}`} style={{ color: ink55 }}>i. The daily ritual</div>
          </div>
          <div className="md:col-span-9">
            <h2
              className={`display-${t.id} text-[36px] sm:text-[52px] md:text-[72px] leading-[1.02]`}
              style={{ fontVariationSettings: "'opsz' 96, 'wght' 420" }}
            >
              Every morning. <span className={`display-italic-${t.id}`}>Three moves.</span> Nothing more.
            </h2>
            <p className="mt-8 text-[17px] md:text-[19px] leading-[1.6] max-w-2xl" style={{ color: ink72 }}>
              You open PintaFlow. It reads your streak, your pipeline, your last week&apos;s numbers — and hands you exactly three growth moves for today. Each with a why, an expected result, a time chip, and a button that opens the right room. No dashboards. No decisions. No overwhelm.
            </p>
          </div>
        </div>

        {/* Three-column mini-card mockup of Today Mode */}
        <div className="md:pl-[25%] grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              badge: "Move 01 · 15 min",
              icon: "🎬",
              title: "Record today's short",
              why: "Every reel is a rep. Keep showing up.",
              expected: "1 short recorded & ready",
              cta: "Start Recording",
            },
            {
              badge: "Move 02 · 10 min",
              icon: "💬",
              title: "Reply to 5 top comments",
              why: "Every reply plants a future fan.",
              expected: "5 personal replies sent",
              cta: "Log Engagement",
            },
            {
              badge: "Move 03 · 5 min",
              icon: "📊",
              title: "Review yesterday's top post",
              why: "Winners hide patterns. Spot yours.",
              expected: "1 insight noted",
              cta: "Open Analytics",
            },
          ].map((m, i) => (
            <div
              key={i}
              data-testid={`landing-move-card-${i}`}
              className="rounded-2xl p-5 flex flex-col gap-3 transition-all hover:-translate-y-0.5"
              style={{ background: `${t.seal}0F`, border: `1px solid ${t.seal}33` }}
            >
              <div className="flex items-center justify-between">
                <span className={`eyebrow-${t.id}`} style={{ color: t.seal, fontSize: 10 }}>
                  {m.badge}
                </span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-2xl leading-none">{m.icon}</span>
                <h4
                  className="text-[19px] leading-tight"
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontVariationSettings: "'opsz' 32, 'wght' 500",
                    color: t.ink,
                  }}
                >
                  {m.title}
                </h4>
              </div>
              <p className="text-[13px] italic leading-relaxed" style={{ color: ink72 }}>
                {m.why}
              </p>
              <div
                className="rounded-lg px-3 py-2 mt-1"
                style={{ background: `${t.seal}08`, border: `1px solid ${t.seal}22` }}
              >
                <div className={`eyebrow-${t.id}`} style={{ color: t.seal, fontSize: 9 }}>
                  Expected result
                </div>
                <div className="text-[13px] mt-0.5" style={{ color: t.ink }}>
                  {m.expected}
                </div>
              </div>
              <button
                onClick={startGoogleLogin}
                className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-full py-2 text-[12px] transition hover:opacity-90"
                style={{ background: t.seal, color: t.bg, fontWeight: 500 }}
                data-testid={`landing-move-cta-${i}`}
              >
                {m.cta} <ArrowUpRight size={11} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>

        {/* Momentum + Prep Tomorrow — two supporting lines */}
        <div className="md:pl-[25%] mt-14 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            className="rounded-2xl p-5"
            style={{ background: `${t.ink}0A`, border: `1px solid ${ink14}` }}
          >
            <div className={`eyebrow-${t.id} mb-2`} style={{ color: ink55 }}>Creator Momentum</div>
            <div
              className="text-[22px] leading-tight"
              style={{
                fontFamily: "'Fraunces', serif",
                fontVariationSettings: "'opsz' 48, 'wght' 500",
                color: t.ink,
              }}
            >
              A single 0-100 score across <em style={{ color: t.seal }}>five</em> dimensions — consistency, planning, engagement, analytics, networking. Feedback, never judgment.
            </div>
          </div>
          <div
            className="rounded-2xl p-5"
            style={{ background: `${t.ink}0A`, border: `1px solid ${ink14}` }}
          >
            <div className={`eyebrow-${t.id} mb-2`} style={{ color: ink55 }}>Prep tomorrow</div>
            <div
              className="text-[22px] leading-tight"
              style={{
                fontFamily: "'Fraunces', serif",
                fontVariationSettings: "'opsz' 48, 'wght' 500",
                color: t.ink,
              }}
            >
              Write your own three moves tonight — and wake up to a plan already made. Or let PintaFlow choose for you. Both work.
            </div>
          </div>
        </div>
      </section>

      <div className="border-t" style={{ borderColor: ink14 }} />

      {/* MANIFESTO */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className={`eyebrow-${t.id}`} style={{ color: ink55 }}>ii. The premise</div>
          </div>
          <div className="md:col-span-9">
            <p
              className={`display-${t.id} text-[32px] sm:text-[44px] md:text-[56px] leading-[1.15]`}
              style={{ fontVariationSettings: "'opsz' 96, 'wght' 400" }}
            >
              We don&apos;t build tools for creators.{" "}
              <span style={{ color: ink55 }}>
                We build the <span className={`display-italic-${t.id}`}>workshop</span>{" "}
                where serious work is kept, refined, and shipped — without the noise of a dozen tools.
              </span>
            </p>
          </div>
        </div>
      </section>

      <div className="border-t" style={{ borderColor: ink14 }} />

      {/* PIPELINE — as list */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="md:col-span-3">
            <div className={`eyebrow-${t.id}`} style={{ color: ink55 }}>iii. The pipeline</div>
          </div>
          <div className="md:col-span-9">
            <h2 className={`display-${t.id} text-[40px] sm:text-[64px] md:text-[88px] leading-[0.98]`}>
              Six rooms.<br />
              <span className={`display-italic-${t.id}`}>One</span> craft.
            </h2>
            <p className="mt-8 text-[17px] md:text-[19px] leading-[1.6] max-w-xl" style={{ color: ink72 }}>
              Every idea moves through the same quiet arc. PintaFlow keeps the rooms — you keep the work.
            </p>
          </div>
        </div>

        <div className="md:pl-[25%]">
          <ol className="relative">
            {STAGES.map(([name, copy], i) => (
              <li
                key={name}
                className="grid grid-cols-12 gap-6 py-7 md:py-9 border-t group"
                style={{ borderColor: ink14 }}
              >
                <div className={`col-span-2 md:col-span-1 eyebrow-${t.id} pt-2`} style={{ color: ink55 }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-10 md:col-span-7">
                  <div
                    className="text-[28px] sm:text-[36px] md:text-[44px] leading-[1.1] tracking-tight"
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontVariationSettings: "'opsz' 72, 'wght' 400",
                      color: t.ink,
                    }}
                  >
                    {name}
                  </div>
                </div>
                <div className="hidden md:flex col-span-4 items-start pt-3">
                  <p className="text-[15px] leading-[1.6]" style={{ color: ink72 }}>
                    {copy}
                  </p>
                </div>
              </li>
            ))}
            <li className="border-t" style={{ borderColor: ink14 }} />
          </ol>
        </div>
      </section>

      <div className="border-t" style={{ borderColor: ink14 }} />

      {/* RESTRAINT */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className={`eyebrow-${t.id}`} style={{ color: ink55 }}>iv. The restraint</div>
          </div>
          <div className="md:col-span-9">
            <h3 className={`display-${t.id} text-[32px] sm:text-[48px] md:text-[64px] leading-[1.06]`}>
              The most luxurious thing we offer is{" "}
              <span className={`display-italic-${t.id}`}>what we left out.</span>
            </h3>
            <ul
              className="mt-14 space-y-7 text-[19px] md:text-[22px] leading-[1.45] max-w-2xl"
              style={{
                color: ink72,
                fontFamily: "'Fraunces', serif",
                fontVariationSettings: "'opsz' 48, 'wght' 400",
              }}
            >
              {[
                "No pushy notifications. Your attention belongs to the work.",
                "No vanity metrics. We measure the causes, not the noise.",
                "No punishing streaks. Rest days are honoured.",
                "No 7-tool chaos. One studio, from idea to publish.",
              ].map((line) => (
                <li key={line} className="flex gap-5">
                  <span className={`eyebrow-${t.id} pt-2 flex-shrink-0`} style={{ color: t.seal }}>—</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <div className="border-t" style={{ borderColor: ink14 }} />

      {/* PRACTICE */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-3">
            <div className={`eyebrow-${t.id}`} style={{ color: ink55 }}>v. The practice</div>
          </div>
          <div className="md:col-span-9">
            <h3 className={`display-${t.id} text-[32px] sm:text-[48px] md:text-[64px] leading-[1.06]`}>
              What stays. <span className={`display-italic-${t.id}`} style={{ color: ink55 }}>The instruments.</span>
            </h3>
          </div>
        </div>

        <div className="md:pl-[25%]">
          {PRINCIPLES.map((p, i) => {
            const [head, tail] = p.split(" — ");
            return (
              <div
                key={p}
                className="grid grid-cols-12 gap-6 py-6 border-t"
                style={{ borderColor: ink14 }}
              >
                <div className={`col-span-2 md:col-span-1 eyebrow-${t.id} pt-2`} style={{ color: ink55 }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div
                  className="col-span-10 md:col-span-11 text-[20px] md:text-[26px] leading-[1.35]"
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontVariationSettings: "'opsz' 48, 'wght' 400",
                    color: t.ink,
                  }}
                >
                  <span>{head}</span>
                  <span style={{ color: ink72 }} className="ml-3">— {tail}</span>
                </div>
              </div>
            );
          })}
          <div className="border-t" style={{ borderColor: ink14 }} />
        </div>
      </section>

      <div className="border-t" style={{ borderColor: ink14 }} />

      {/* CLOSING */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-44 text-center">
        <div className={`eyebrow-${t.id} mb-10 flex items-center justify-center gap-3`} style={{ color: ink55 }}>
          <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: t.seal }} />
          The invitation
        </div>
        <h2 className={`display-${t.id} text-[48px] sm:text-[80px] md:text-[112px] leading-[0.96] max-w-4xl mx-auto`}>
          Make space<br />
          for the <span className={`display-italic-${t.id}`}>work.</span>
        </h2>
        <div className="mt-16 inline-flex flex-col items-center gap-4">
          <button
            onClick={startGoogleLogin}
            className="group inline-flex items-center justify-between rounded-full pl-6 pr-4 py-3.5 transition hover:opacity-90"
            style={{ background: t.ink, color: t.bg }}
          >
            <span className="text-[14px] font-medium tracking-tight">Open the studio</span>
            <span
              className="w-7 h-7 rounded-full ml-6 flex items-center justify-center transition group-hover:rotate-45"
              style={{ background: t.seal, color: t.bg }}
            >
              <ArrowUpRight size={13} strokeWidth={1.75} />
            </span>
          </button>
          <span className={`eyebrow-${t.id}`} style={{ color: ink55 }}>
            Sign in with Google · Free in private beta
          </span>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t" style={{ borderColor: ink14 }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex items-center gap-3">
              <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: t.seal }} />
              <span
                className="text-[15px]"
                style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 24, 'wght' 500" }}
              >
                PintaFlow
              </span>
              <span className={`eyebrow-${t.id} ml-4`} style={{ color: ink55 }}>est. 2026</span>
              <span
                className="text-[10px] uppercase tracking-[0.24em] ml-3 px-2.5 py-1 rounded-full"
                style={{ background: "rgba(216,168,87,0.10)", border: `1px solid ${t.seal}55`, color: t.seal }}
              >
                Private Beta
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px]" style={{ color: ink55 }}>
              <a href="/about" className="hover:opacity-80 transition" data-testid="footer-about-link">About</a>
              <a href="/pricing" className="hover:opacity-80 transition" data-testid="footer-pricing-link">Pricing</a>
              <a href="/faq" className="hover:opacity-80 transition" data-testid="footer-faq-link">FAQ</a>
              <a href="/contact" className="hover:opacity-80 transition" data-testid="footer-contact-link">Contact</a>
              <a href="/privacy" className="hover:opacity-80 transition" data-testid="footer-privacy-link">Privacy</a>
              <a href="/terms" className="hover:opacity-80 transition" data-testid="footer-terms-link">Terms</a>
              <a href="/refund" className="hover:opacity-80 transition" data-testid="footer-refund-link">Refund</a>
              <a href="/cookies" className="hover:opacity-80 transition" data-testid="footer-cookies-link">Cookies</a>
              <a href="/shipping" className="hover:opacity-80 transition" data-testid="footer-shipping-link">Delivery</a>
              <a
                href="mailto:hello@pintaflow.com"
                className="hover:opacity-80 transition"
                data-testid="footer-email-link"
                style={{ color: t.seal }}
              >
                hello@pintaflow.com
              </a>
            </div>
          </div>
          <div className={`eyebrow-${t.id} mt-8 pt-6`} style={{ color: ink55, borderTop: `1px solid ${ink14}` }}>
            © 2026 PintaFlow · Built quietly by a creator for creators
          </div>
        </div>
      </footer>
      {showVideo && (
        <WalkthroughModal
          theme={t}
          onClose={() => setShowVideo(false)}
          onTryDemo={() => { setShowVideo(false); startGuestDemo(); }}
        />
      )}
    </div>
  );
}
